import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'

interface LogoProps {
    size?: number
    className?: string
}

interface BrandLockupProps {
    size?: number
    tone?: 'dark' | 'light'
    subtitle?: string | false
    className?: string
}

// The GKV mark, drawn on a 64 x 64 grid: a bold geometric "G" on a glowing
// indigo-to-emerald tile, with the golden spark of knowledge rising from its
// opening. Pure vector, so it stays sharp from the browser tab to a poster.
const TILE = { x: 2, y: 2, size: 60, radius: 18 }
const LETTER_G = 'M40.05 24.95A13.5 13.5 0 1 0 44 34.5H31.5'
const SPARK = 'M47 9.5Q47.9 16.1 54.5 17Q47.9 17.9 47 24.5Q46.1 17.9 39.5 17Q46.1 16.1 47 9.5Z'

const MARK_SVG = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'><defs><linearGradient id='b' x1='0' y1='0' x2='64' y2='64' gradientUnits='userSpaceOnUse'><stop offset='0' stop-color='#818CF8'/><stop offset='0.45' stop-color='#4F46E5'/><stop offset='1' stop-color='#1E1B4B'/></linearGradient><radialGradient id='e' cx='58' cy='60' r='40' gradientUnits='userSpaceOnUse'><stop offset='0' stop-color='#10B981' stop-opacity='0.8'/><stop offset='1' stop-color='#10B981' stop-opacity='0'/></radialGradient><radialGradient id='h' cx='10' cy='4' r='38' gradientUnits='userSpaceOnUse'><stop offset='0' stop-color='#FFFFFF' stop-opacity='0.38'/><stop offset='1' stop-color='#FFFFFF' stop-opacity='0'/></radialGradient><linearGradient id='s' x1='47' y1='9.5' x2='47' y2='24.5' gradientUnits='userSpaceOnUse'><stop offset='0' stop-color='#FEF3C7'/><stop offset='0.5' stop-color='#FCD34D'/><stop offset='1' stop-color='#F59E0B'/></linearGradient></defs><rect x='2' y='2' width='60' height='60' rx='18' fill='url(#b)'/><rect x='2' y='2' width='60' height='60' rx='18' fill='url(#e)'/><rect x='2' y='2' width='60' height='60' rx='18' fill='url(#h)'/><path d='${LETTER_G}' fill='none' stroke='#FFFFFF' stroke-width='8' stroke-linecap='round' stroke-linejoin='round'/><path d='${SPARK}' fill='url(#s)'/></svg>`

export const logoMarkDataUri = `data:image/svg+xml;utf8,${encodeURIComponent(MARK_SVG)}`

export const Logo: FC<LogoProps> = ({ size = 42, className }): ReactNode => {
    // A unique id per instance, so a hidden copy of the logo never takes its
    // gradients with it.
    const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    const ref = (name: string) => `gkv-${name}-${uid}`
    const tile = { x: TILE.x, y: TILE.y, width: TILE.size, height: TILE.size, rx: TILE.radius }

    return (
        <svg
            role='img'
            aria-label='GKV Tech Campus'
            viewBox='0 0 64 64'
            width={size}
            height={size}
            className={cn('block shrink-0 select-none drop-shadow-[0_8px_18px_rgba(79,70,229,0.45)]', className)}
        >
            <defs>
                <linearGradient id={ref('base')} x1='0' y1='0' x2='64' y2='64' gradientUnits='userSpaceOnUse'>
                    <stop offset='0' stopColor='#818CF8' />
                    <stop offset='0.45' stopColor='#4F46E5' />
                    <stop offset='1' stopColor='#1E1B4B' />
                </linearGradient>

                <radialGradient id={ref('emerald')} cx='58' cy='60' r='40' gradientUnits='userSpaceOnUse'>
                    <stop offset='0' stopColor='#10B981' stopOpacity='0.8' />
                    <stop offset='1' stopColor='#10B981' stopOpacity='0' />
                </radialGradient>

                <radialGradient id={ref('shine')} cx='10' cy='4' r='38' gradientUnits='userSpaceOnUse'>
                    <stop offset='0' stopColor='#FFFFFF' stopOpacity='0.38' />
                    <stop offset='1' stopColor='#FFFFFF' stopOpacity='0' />
                </radialGradient>

                <radialGradient id={ref('glow')} cx='47' cy='17' r='11' gradientUnits='userSpaceOnUse'>
                    <stop offset='0' stopColor='#FDE68A' stopOpacity='0.6' />
                    <stop offset='1' stopColor='#FDE68A' stopOpacity='0' />
                </radialGradient>

                <linearGradient id={ref('spark')} x1='47' y1='9.5' x2='47' y2='24.5' gradientUnits='userSpaceOnUse'>
                    <stop offset='0' stopColor='#FEF3C7' />
                    <stop offset='0.5' stopColor='#FCD34D' />
                    <stop offset='1' stopColor='#F59E0B' />
                </linearGradient>
            </defs>

            <rect {...tile} fill={`url(#${ref('base')})`} />
            <rect {...tile} fill={`url(#${ref('emerald')})`} />
            <rect {...tile} fill={`url(#${ref('shine')})`} />
            <rect x='2.6' y='2.6' width='58.8' height='58.8' rx='17.4' fill='none' stroke='#FFFFFF' strokeOpacity='0.26' strokeWidth='1.2' />

            <path d={LETTER_G} transform='translate(0 1.6)' fill='none' stroke='#1E1B4B' strokeOpacity='0.35' strokeWidth='7.5' strokeLinecap='round' strokeLinejoin='round' />
            <path d={LETTER_G} fill='none' stroke='#FFFFFF' strokeWidth='7.5' strokeLinecap='round' strokeLinejoin='round' />

            <circle cx='47' cy='17' r='11' fill={`url(#${ref('glow')})`} />
            <path d={SPARK} fill={`url(#${ref('spark')})`} />
            <circle cx='55.2' cy='27.2' r='1.5' fill='#FDE68A' fillOpacity='0.9' />
        </svg>
    )
}

// The full brand signature: the mark, the GKV wordmark with TECH CAMPUS in
// spaced capitals, and an optional line underneath.
export const BrandLockup: FC<BrandLockupProps> = ({ size = 42, tone = 'dark', subtitle = 'FET · Gurukula Kangri University', className }): ReactNode => {
    const dark = tone === 'dark'

    return (
        <span className={cn('flex min-w-0 items-center', className)} style={{ gap: Math.round(size * 0.3) }}>
            <Logo size={size} />

            <span className='min-w-0 leading-none'>
                <span className='flex items-baseline' style={{ gap: Math.max(5, Math.round(size * 0.14)) }}>
                    <span className={cn('font-black tracking-[-0.035em]', dark ? 'text-white' : 'text-ink')} style={{ fontSize: Math.round(size * 0.48) }}>
                        GKV
                    </span>

                    <span
                        className={cn('whitespace-nowrap bg-gradient-to-r bg-clip-text font-extrabold tracking-[0.22em] text-transparent', dark ? 'from-indigo-200 via-sky-200 to-emerald-200' : 'from-indigo-600 via-violet-600 to-emerald-600')}
                        style={{ fontSize: Math.max(9.5, Math.round(size * 0.26 * 10) / 10) }}
                    >
                        TECH CAMPUS
                    </span>
                </span>

                {subtitle && (
                    <span
                        className={cn('mt-[0.45em] block truncate font-semibold uppercase tracking-[0.16em]', dark ? 'text-slate-400' : 'text-slate-500')}
                        style={{ fontSize: Math.max(9, Math.round(size * 0.22 * 10) / 10) }}
                    >
                        {subtitle}
                    </span>
                )}
            </span>
        </span>
    )
}
